import ImageArrowRight from "components/utility/images/ImageArrowRight";
import ImageArrowLeft from "components/utility/images/ImageArrowLeft";
import ImageX from "components/utility/images/ImageX";
import { setGALocation } from "./utility/setGALocation";
import { useRef } from "react";
import {
	changeCountry,
	financialProfessionalGUID,
	getAudienceSelection,
	individualGUID
} from "./utility/audienceSortApiHandlers";
import { audienceSortStepNames } from "./utility/audienceSortStepNames";
import { AudienceSelectorMobileProps } from "./utility/audienceSortPropTypes";

const AudienceSelectorMobile: React.FC<AudienceSelectorMobileProps> = ({
	currentPage,
	audienceTypesData,
	countryListData,
	setAudienceSelectionData,
	cameFromInitialStep = false,
	setStep,
	setIsModalOpen,
	setCountryListData,
	setModalJustOpened,
	audienceSortTheme,
	customCloseHandler,
	hasCloseButton = false
}) => {
	const audienceSortBackButton = useRef<HTMLDivElement>(null);

	const closeModal = (event: React.MouseEvent | React.KeyboardEvent) => {
		event?.stopPropagation();
		if (customCloseHandler) {
			customCloseHandler();
		} else {
			if (setIsModalOpen) setIsModalOpen(false);
			setStep(audienceSortStepNames.initialStep);
		}
	};

	const handleBackButton = (): void => {
		if (cameFromInitialStep) {
			setStep(audienceSortStepNames.notSelecting);
			if (setIsModalOpen) setIsModalOpen(false);
		} else {
			setStep(audienceSortStepNames.countrySelection);
		}
	};

	const handleAudienceSelection = async (
		event: React.MouseEvent | React.KeyboardEvent,
		item
	): Promise<void> => {
		await getAudienceSelection(
			event,
			item,
			cameFromInitialStep ? null : "redirect",
			audienceTypesData,
			setAudienceSelectionData,
			currentPage
		);
		if (item.audienceTypeId === financialProfessionalGUID) {
			setStep(audienceSortStepNames.financialProfessionalAffirmation);
		}
	};

	const handleCountrySwitch = async (
		event: React.MouseEvent | React.KeyboardEvent
	): Promise<void> => {
		if (!countryListData) {
			await changeCountry(event, audienceTypesData, setCountryListData);
		}
		if (setModalJustOpened) setModalJustOpened(false);
		setStep(audienceSortStepNames.countrySelection);
	};

	return (
		<div className={"audience-selector-mobile-container " + audienceSortTheme}>
			{!cameFromInitialStep && (
				<div className="audience-selector-mobile-intro" data-qa="audience-selector-mobile-intro">
					<div
						className="audience-selector-mobile-intro-back-button"
						role="button"
						tabIndex={0}
						onClick={handleBackButton}
						onKeyDown={handleBackButton}
					>
						<ImageArrowLeft />
						{audienceTypesData.locationText ? (
							<div className={"audience-selector-mobile-intro-label"}>
								{audienceTypesData.locationText}
							</div>
						) : null}
					</div>

					{hasCloseButton && (
						<button
							className="audience-selector-mobile-close-button"
							aria-label="Close"
							onClick={closeModal}
						>
							<ImageX />
						</button>
					)}
				</div>
			)}
			<div
				className="audience-selector-mobile-intro"
				role="button"
				tabIndex={currentPage === "global-splash" ? -1 : 0}
				ref={audienceSortBackButton}
				data-qa="audience-selector-mobile-intro"
				onClick={currentPage === "nav" && cameFromInitialStep ? handleBackButton : undefined}
				onKeyDown={currentPage === "nav" && cameFromInitialStep ? handleBackButton : undefined}
			>
				<div className="audience-selector-mobile-intro-back-button">
					{cameFromInitialStep && <ImageArrowLeft />}
					{audienceTypesData.userSelectionText && (
						<h3
							className={
								cameFromInitialStep
									? "audience-selector-mobile-intro-label"
									: "audience-selector-mobile-intro-title"
							}
						>
							{audienceTypesData.userSelectionText}
						</h3>
					)}
				</div>
				{cameFromInitialStep && hasCloseButton && (
					<button
						className="country-selector-mobile-close-button"
						aria-label="Close"
						onClick={closeModal}
					>
						<ImageX />
					</button>
				)}
			</div>
			<ul className="audience-selector-mobile-items">
				{audienceTypesData.userSelectionText && audienceTypesData.sectionAudienceType.length > 0
					? audienceTypesData.sectionAudienceType.map((item) => {
							return (
								<li
									key={item.title}
									className={`audience-selector-mobile-items-item ${
										item.isActive &&
										// The only time there won't be countryListData is before the user tries to change their country
										audienceTypesData.countryKey ===
											(countryListData?.countryKey || audienceTypesData.countryKey) &&
										currentPage !== "unknown-splash"
											? "active"
											: ""
									}`}
								>
									<button
										data-a="true"
										data-a-lbl={item.title}
										data-a-cat={currentPage === "nav" ? "Header" : "Audience"}
										data-a-act={currentPage === "nav" ? "Audience Switcher" : "Audience Selector"}
										data-a-evt={currentPage === "nav" ? "Audience Switch" : "Audience Select"}
										data-a-loc={setGALocation(currentPage)}
										data-a-text={item.title}
										data-a-comp="audience switcher modal"
										data-qa={
											(item.audienceTypeId === financialProfessionalGUID &&
												"audience-button-professional") ||
											(item.audienceTypeId === individualGUID && "audience-button-individual")
										}
										aria-label={item.title}
										tabIndex={0}
										onClick={async (event) => {
											handleAudienceSelection(event, item);
										}}
									>
										<div className="audience-selector-mobile-items-item-title">{item.title}</div>
										{item.isAffirmationRequired && <ImageArrowRight />}
									</button>
								</li>
							);
						})
					: null}
			</ul>
			{currentPage !== "global-splash" && cameFromInitialStep && (
				<div
					className="audience-selector-mobile-location"
					role="button"
					tabIndex={0}
					onKeyUp={handleCountrySwitch}
					onClick={handleCountrySwitch}
					data-qa="audience-link-locationchange"
					aria-label={audienceTypesData.countrySelection.title}
				>
					<p>{audienceTypesData.countrySelection.title}</p>
					<ImageArrowRight />
				</div>
			)}
		</div>
	);
};

export default AudienceSelectorMobile;
